// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-member-access, react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars, @typescript-eslint/strict-boolean-expressions */

import {
  Box,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  Typography,
  Icon,
} from '@mui/material';

import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import OhlcIcon from 'icons/OHLC';

import { HiroInlineControls, PutCallButton } from './HiroInlineControls';
import Button from '@mui/material/Button';
import {
  DefaultChartSettings,
  MARK_KEYS,
  MARKS,
  VAL2MARK,
} from '../../../../config';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import {
  businessDaysSubtract,
  dayjs,
  ET,
  getDefaultSlotProps,
  getQueryDate,
  getEnv,
} from '../../../../util';
import {
  BottomCandlesType,
  HiroUpdatingType,
  ProductType,
  ValToMarkerKey,
  HIRO_LINE_TYPES,
} from '../../../../types';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { hiroUpdatingTypeState, userDashAccessState } from '../../../../states';
import { useHiroCtx } from '../../HiroProvider';
import useUserDetails from '../../../../hooks/user/useUserDetails';
import { useTheme } from '@mui/material/styles';
import useAuth from '../../../../hooks/auth/useAuth';
import { SGTooltip } from '../../../core';
import { hasDashAccess, DashAccess } from 'types/user';
import { HISTORY_BEGIN } from '../../HiroDatafeed';
import { isMarketOpenOnDate } from 'util/shared/date';

type ChartSettingsProps = {
  onClose?: () => void;
};

const CANDLE_DURATIONS = [
  { label: '5s', value: 5 },
  { label: '10s', value: 10 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '2m', value: 2 * 60 },
  { label: '5m', value: 5 * 60 },
  { label: '10m', value: 10 * 60 },
  { label: '15m', value: 15 * 60 },
  { label: '30m', value: 30 * 60 },
];

const CANDLE_DURATION_LABELS_AND_INDICES = CANDLE_DURATIONS.map(
  ({ label }, idx) => ({ label, value: idx }),
);

const CANDLE_DURATION_VAL_FROM_IDX = new Map(
  CANDLE_DURATIONS.map(({ value }, idx) => [idx, value]),
);

const CANDLE_DURATION_IDX_FROM_VAL = new Map(
  CANDLE_DURATIONS.map(({ value }, idx) => [value, idx]),
);

export const ChartSettings = ({ onClose }: ChartSettingsProps) => {
  const dashAccess = useRecoilValue(userDashAccessState);
  const { productsWithAccess } = useAuth();
  const theme = useTheme();

  const {
    showAlerts,
    hiroRollingWindowSeconds: sumWindow,
    startQueryDate: startDate,
    endQueryDate: endDate,
    lastSetDate,
    includeHiroETH,
    showHiroKeyLevels,
    candleDuration,
    hiroLineType,
    showChartTooltip,
    bottomCandlesType,
    enableHiroV2: newTVHiro,
    enableHiroV3,
    update,
  } = useHiroCtx();

  const defaultSlotProps = getDefaultSlotProps(theme);
  const hasHiro = productsWithAccess.has(ProductType.HIRO);

  const maxQueryDate = useMemo(() => dayjs(getQueryDate(true).format()), []);

  const minQueryDate = useMemo(
    () =>
      enableHiroV3 ? HISTORY_BEGIN : businessDaysSubtract(maxQueryDate, 4),
    [maxQueryDate, enableHiroV3],
  );
  const hiroUpdatingType = useRecoilValue(hiroUpdatingTypeState);

  const markSetDateToday = () => {
    const today = dayjs().tz(ET);
    if (!lastSetDate?.isSame(today, 'day')) {
      update({ lastSetDate: today });
    }
  };

  const saveSumWindow = (v: string) => {
    const newWin = VAL2MARK[v as ValToMarkerKey].seconds;
    if (newWin !== sumWindow) {
      update({ hiroRollingWindowSeconds: newWin });
    }
  };

  const resetToDefaults = useCallback(() => {
    update({ startQueryDate: getQueryDate(), endQueryDate: getQueryDate() });
    markSetDateToday();
    update(DefaultChartSettings);
  }, [update]);

  const getSumWindowKey = () => {
    for (const key in VAL2MARK) {
      const seconds = VAL2MARK[key as ValToMarkerKey].seconds;
      if (seconds === sumWindow) {
        return parseInt(key);
      }
    }

    return 7; // default settings sum window key
  };

  return (
    <Box
      sx={{
        width: 'auto',
        height: 'auto',
        backgroundColor: theme.palette.background.paper,
        marginTop: '10px',
        flexDirection: 'column',
        display: 'flex',
        padding: '16px',
        boxShadow: theme.palette.shadows.default,
        borderRadius: theme.spacing(3),
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          marginBottom: '2ex',
        }}
      >
        <PutCallButton />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          sx={{
            fontFamily: 'SF Pro Display',
            fontSize: 12,
            textTransform: 'capitalize',
            backgroundColor: 'transparent',
          }}
          onClick={resetToDefaults}
        >
          Reset to defaults
        </Button>
      </Box>

      <Grid
        container
        direction={'column'}
        justifyContent="space-around"
        alignItems="flex-start"
      >
        <Grid item>
          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            <HiroInlineControls isInline={false} />
          </Box>

          <Box sx={{ width: 300, marginTop: '15px' }}>
            <Typography sx={{ fontSize: '14px', my: '8px' }}>
              Rolling Window
            </Typography>
            <Slider
              aria-label="Rolling Window"
              value={getSumWindowKey()}
              step={null}
              onChange={(e: Event, v: number | number[]) => {
                saveSumWindow(v.toString());
              }}
              marks={MARKS}
              min={parseInt(MARK_KEYS[0])}
              max={parseInt(MARK_KEYS[MARK_KEYS.length - 1])}
            />
          </Box>

          {!enableHiroV3 ? null : (
            <Box sx={{ width: 300, marginTop: '15px' }}>
              <Typography sx={{ fontSize: '14px', my: '8px' }}>
                Candle Duration
              </Typography>
              <Slider
                aria-label="Candle Duration"
                value={CANDLE_DURATION_IDX_FROM_VAL.get(candleDuration)}
                step={null}
                onChange={(e: Event, v: number) =>
                  update({
                    candleDuration: CANDLE_DURATION_VAL_FROM_IDX.get(v),
                  })
                }
                marks={CANDLE_DURATION_LABELS_AND_INDICES}
                min={CANDLE_DURATION_LABELS_AND_INDICES[0].value}
                max={
                  CANDLE_DURATION_LABELS_AND_INDICES[
                    CANDLE_DURATION_LABELS_AND_INDICES.length - 1
                  ].value
                }
              />
            </Box>
          )}
        </Grid>
        <Box sx={{ marginBottom: 6 }}>
          <Typography sx={{ fontSize: '14px' }}>Date Range</Typography>
        </Box>

        <Grid item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['day']}
              label="Start Date"
              value={startDate}
              disabled={!hasHiro}
              onChange={(newValue: Dayjs | null) => {
                if (newValue && newValue.isValid()) {
                  update({
                    startQueryDate: newValue,
                    ...(endDate < newValue
                      ? {
                          endQueryDate: newValue,
                        }
                      : {}),
                  });
                }
              }}
              minDate={minQueryDate}
              maxDate={maxQueryDate}
              shouldDisableDate={(d) => !isMarketOpenOnDate(dayjs(d))}
              format="YYYY-MM-DD"
              slotProps={{
                ...defaultSlotProps,
                textField: {
                  ...defaultSlotProps.textField,
                  style: {
                    width: '170px',
                    fontSize: '14px',
                    marginBottom: '12px',
                    marginRight: '5px',
                  },
                },
              }}
            />
            <DatePicker
              views={['day']}
              label="End Date"
              value={endDate}
              disabled={!hasHiro}
              onChange={(newValue: Dayjs | null) => {
                if (newValue && newValue.isValid()) {
                  update({
                    endQueryDate: newValue,
                    ...(newValue < startDate
                      ? {
                          startQueryDate: newValue,
                        }
                      : {}),
                  });
                }
              }}
              minDate={minQueryDate}
              maxDate={maxQueryDate}
              shouldDisableDate={(d) => !isMarketOpenOnDate(dayjs(d))}
              format="YYYY-MM-DD"
              slotProps={{
                ...defaultSlotProps,
                textField: {
                  ...defaultSlotProps.textField,
                  style: {
                    width: '170px',
                    fontSize: '14px',
                    marginBottom: '12px',
                    marginLeft: '5px',
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item sx={{ display: 'flex' }}>
          <Grid
            item
            sx={{
              flexDirection: enableHiroV3 ? 'row' : 'column',
              display: 'flex',
              gap: '8px',
            }}
          >
            <FormControlLabel
              value="bottom"
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={showAlerts}
                  onChange={(evt) => {
                    update({
                      showAlerts: evt.target.checked,
                    });
                  }}
                  sx={{ fontSize: '14px' }}
                />
              }
              label="Show Alerts"
              labelPlacement="end"
            />

            <FormControlLabel
              value="bottom"
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={includeHiroETH}
                  onChange={(evt) => {
                    update({
                      includeHiroETH: evt.target.checked,
                    });
                  }}
                  sx={{ fontSize: '14px' }}
                />
              }
              label="Extended Hours"
              labelPlacement="end"
            />

            <FormControlLabel
              value="bottom"
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={showHiroKeyLevels}
                  onChange={(evt) => {
                    update({
                      showHiroKeyLevels: evt.target.checked,
                    });
                  }}
                  sx={{ fontSize: '14px' }}
                />
              }
              label="Key Levels"
              labelPlacement="end"
            />

            {hiroUpdatingType === HiroUpdatingType.STREAMING && (
              <SGTooltip
                title={
                  'Show a tooltip displaying all the values for the hovered over candle. This is resource intensive and may slow down the HIRO chart.'
                }
              >
                <FormControlLabel
                  value="bottom"
                  control={
                    <Switch
                      size="small"
                      color="primary"
                      checked={showChartTooltip}
                      onChange={(evt) => {
                        update({
                          showChartTooltip: evt.target.checked,
                        });
                      }}
                      sx={{ fontSize: '14px' }}
                    />
                  }
                  label="Show corner tooltip"
                  labelPlacement="end"
                  sx={{ marginBottom: '12px' }}
                />
              </SGTooltip>
            )}
          </Grid>

          {hiroUpdatingType === HiroUpdatingType.STREAMING && (
            <FormControl>
              <FormLabel
                id="bottom-candles-data-label"
                sx={{
                  color: `${theme.palette.text.primary} !important`,
                }}
              >
                Bottom Chart Display
              </FormLabel>
              <RadioGroup
                aria-labelledby="bottom-candles-data-label"
                value={bottomCandlesType}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  update({
                    bottomCandlesType: event.target.value as BottomCandlesType,
                  });
                }}
              >
                <FormControlLabel
                  value={BottomCandlesType.ABSOLUTE_DELTA}
                  control={<Radio />}
                  label="Absolute Delta"
                />
                <FormControlLabel
                  value={BottomCandlesType.FILTERED_DELTA}
                  control={<Radio />}
                  label="Net HIRO Value"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Grid>

        <Grid item sx={{ width: '100%', marginTop: 2 }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              float: 'right',
              width: '45px',
              fontFamily: 'Satoshi Complete',
              fontSize: 12,
              textTransform: 'capitalize',
            }}
            onClick={onClose}
          >
            Close
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
